/**
 * O motor dos fluxos: quem o gatilho alcança, quem sobrevive às condições e o
 * que cada ação faria.
 *
 * Fica fora dos adaptadores porque a simulação e a execução precisam concordar
 * — o que a tela mostra que vai acontecer é exatamente o que acontece ao
 * confirmar. Duas implementações divergiriam justamente no caso que ninguém
 * testou.
 *
 * **O alcance sai das mesmas consultas das telas.** Quem não voltou vem de
 * `retornosEmAberto`, o vencido vem do financeiro, o produto em falta vem do
 * estoque. Uma consulta própria daqui daria um número diferente do que a
 * clínica vê no resto do sistema.
 */

import type { PortaDeDados } from './porta';
import { condicaoPassa, acaoDoFluxo } from './fluxos';
import type { PassoDeAcao, PassoDeAgente, PassoDoFluxo } from './fluxos';
import { executarProposta } from './propostasDoAgente';
import type { AcaoSimulada, CasoSimulado, Fluxo, GatilhoDeAutomacao, RascunhoDoAgente, ResultadoDoFluxo, SimulacaoDoFluxo } from './tipos';

interface Caso {
    rotulo: string;
    campos: Record<string, unknown>;
}

/**
 * Quantos casos o passo de agente redige por execução.
 *
 * Cada caso é uma chamada ao provedor, paga pela chave da plataforma e contada
 * no teto da clínica. Um gatilho que alcança trezentas pessoas não pode virar
 * trezentas chamadas num clique.
 */
export const CASOS_POR_PASSO_DE_AGENTE = 10;

/** O que identifica a pessoa não sobe para o provedor de IA. */
const CAMPOS_PESSOAIS = new Set(['nome', 'telefone', 'paciente', 'contraparte']);

/** Os casos que o gatilho alcança agora, com os campos que a condição examina. */
export async function casosDoGatilho(porta: PortaDeDados, gatilho: GatilhoDeAutomacao): Promise<Caso[]> {
    if (gatilho === 'sem-retorno-90d') {
        const retornos = await porta.retornosEmAberto(90);
        return retornos.map((r) => ({
            rotulo: r.nome,
            campos: {
                nome: r.nome,
                telefone: r.telefone,
                diasSemRetorno: r.diasSemRetorno,
                ultimoProcedimento: r.ultimoProcedimento,
            },
        }));
    }

    if (gatilho === 'titulo-vencido') {
        const financeiro = await porta.financeiro({ aba: 'receber', porPagina: 500 });
        const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
        return financeiro.titulos
            .filter((t) => t.vencimento < hoje && t.pagoCentavos < t.valorCentavos)
            .map((t) => ({
                rotulo: `${t.contraparte} · ${t.descricao}`,
                campos: {
                    contraparte: t.contraparte,
                    descricao: t.descricao,
                    valorReais: Math.round(t.valorCentavos) / 100,
                    diasDeAtraso: Math.round(
                        (new Date(`${hoje}T12:00:00-03:00`).getTime() - new Date(`${t.vencimento}T12:00:00-03:00`).getTime()) / 86400000,
                    ),
                },
            }));
    }

    if (gatilho === 'estoque-minimo') {
        const estoque = await porta.estoque({ aba: 'baixo', porPagina: 500 });
        return estoque.produtos.map((p) => ({
            rotulo: p.nome,
            campos: { nome: p.nome, disponivel: p.disponivel, minimo: p.minimo },
        }));
    }

    if (gatilho === 'agendamento-24h') {
        const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
        const semana = await porta.agendaDaSemana(hoje);
        const agora = Date.now();
        return semana.agendamentos
            .filter((a) => {
                const inicio = new Date(a.inicioEm).getTime();
                return a.status !== 'bloqueio' && a.status !== 'cancelado'
                    && inicio > agora && inicio <= agora + 24 * 60 * 60 * 1000;
            })
            .map((a) => ({
                rotulo: `${a.paciente} · ${a.procedimento}`,
                campos: { paciente: a.paciente, procedimento: a.procedimento, quando: a.inicioEm },
            }));
    }

    /* Gatilho de EVENTO não tem fila: ele dispara no instante em que a coisa
       acontece, e simular "quantos agora" seria responder outra pergunta. */
    return [];
}

/** `{campo}` vira o valor do caso. É o que faz a ação falar do caso certo. */
export function preencher(texto: string, campos: Record<string, unknown>): string {
    return texto.replace(/\{(\w+)\}/g, (inteiro, campo: string) => {
        const valor = campos[campo];
        return valor === undefined || valor === null ? inteiro : String(valor);
    });
}

/**
 * O que o passo de agente pede ao provedor sobre um caso.
 *
 * Só os números do caso: nome e telefone ficam na clínica, e o texto volta com
 * `{nome}` no lugar — quem confere o rascunho sabe de quem se trata.
 */
export function pedidoAoAgente(passo: PassoDeAgente, campos: Record<string, unknown>): string {
    const dados = Object.entries(campos)
        .filter(([campo]) => !CAMPOS_PESSOAIS.has(campo))
        .map(([campo, valor]) => `${campo}: ${String(valor)}`)
        .join('; ');
    return [
        'Pedido de um fluxo da clínica — não é mensagem de paciente.',
        `Instrução: ${passo.instrucao}`,
        dados ? `Dados do caso: ${dados}.` : 'O caso não tem dados além do gatilho.',
        'Escreva {nome} onde entraria o nome da pessoa. Qualquer ação que você propuser será conferida por alguém da clínica antes de acontecer.',
    ].join('\n');
}

/**
 * A ação vale por CASO ou por CLÍNICA?
 *
 * Follow-up e mensagem falam de uma pessoa — um por caso. Régua de lembrete é
 * configuração da clínica: criar uma por paciente alcançado encheria a régua de
 * cópias da mesma linha.
 */
export function porCaso(acao: PassoDeAcao['acao']): boolean {
    return acao !== 'regua_criar';
}

/**
 * Quem chega vivo até o passo de índice `indice`.
 *
 * A ordem do desenho é a ordem da execução: uma condição só filtra o que vem
 * DEPOIS dela. Quem para na condição do meio já recebeu as ações anteriores —
 * e é por isso que a conta é por posição, e não um "passou em tudo" global.
 */
function alvosNoPasso(casos: CasoSimulado[], indice: number): CasoSimulado[] {
    return casos.filter((c) => c.parouEm === undefined || c.parouEm > indice);
}

export async function simular(porta: PortaDeDados, fluxo: Fluxo): Promise<SimulacaoDoFluxo> {
    const passos = (fluxo.passos ?? []) as PassoDoFluxo[];
    const brutos = await casosDoGatilho(porta, fluxo.gatilho);

    const casos: CasoSimulado[] = brutos.map((c) => {
        for (let i = 0; i < passos.length; i += 1) {
            const passo = passos[i];
            if (!passo || passo.tipo !== 'condicao') continue;
            if (!condicaoPassa(c.campos, passo)) {
                return {
                    rotulo: c.rotulo,
                    campos: c.campos,
                    alcancado: false,
                    parouEm: i,
                    motivo: `Parou na condição: ${passo.campo} ${passo.operador} ${passo.valor}`,
                };
            }
        }
        return { rotulo: c.rotulo, campos: c.campos, alcancado: true };
    });

    const problemas: SimulacaoDoFluxo['problemas'] = [];

    const acoes: AcaoSimulada[] = passos
        .map((passo, indice) => ({ passo, indice }))
        .filter((x): x is { passo: PassoDeAcao | PassoDeAgente; indice: number } => x.passo.tipo === 'acao' || x.passo.tipo === 'agente')
        .map(({ passo, indice }) => {
            const alvos = alvosNoPasso(casos, indice);

            if (passo.tipo === 'agente') {
                /* O MESMO número que a execução usa: a simulação dizia que o
                   agente rodava, e a execução pulava o passo. */
                const quantos = Math.min(CASOS_POR_PASSO_DE_AGENTE, alvos.length);
                if (alvos.length > CASOS_POR_PASSO_DE_AGENTE) {
                    problemas.push({
                        passo: passo.id,
                        mensagem: `O agente redige para os primeiros ${CASOS_POR_PASSO_DE_AGENTE} de ${alvos.length} casos por execução.`,
                    });
                }
                return {
                    passo: passo.id,
                    rotulo: 'Rascunho do agente',
                    quantos,
                    ...(quantos ? { exemplo: { instrucao: passo.instrucao } } : {}),
                };
            }

            const acao = acaoDoFluxo(passo.acao);
            const quantos = porCaso(passo.acao) ? alvos.length : Math.min(1, alvos.length);
            const exemplo = alvos[0]
                ? Object.fromEntries(
                    Object.entries(passo.campos).map(([campo, valor]) => [campo, preencher(valor, alvos[0]!.campos)]),
                )
                : undefined;
            return {
                passo: passo.id,
                rotulo: acao?.rotulo ?? passo.acao,
                quantos,
                ...(acao?.bloqueio ? { bloqueio: acao.bloqueio } : {}),
                ...(exemplo ? { exemplo } : {}),
            };
        });

    if (passos.some((p) => p.tipo === 'espera')) {
        problemas.push({ mensagem: 'Os passos de espera não rodam: o fluxo ainda não guarda em que passo cada caso está, e o caso para na espera.' });
    }
    if (passos.some((p) => p.tipo === 'agente')) {
        problemas.push({
            mensagem: 'Ao executar, o agente redige um rascunho por caso — cada um gasta a chave da plataforma — e o que ele propuser só acontece depois de alguém confirmar. A simulação não chama o agente.',
        });
    }

    return {
        gatilho: fluxo.gatilho,
        alcanceBruto: brutos.length,
        casos,
        acoes,
        problemas,
    };
}

/**
 * Executa o fluxo para quem passou nas condições.
 *
 * As ações internas passam por `executarProposta`, o mesmo caminho da
 * confirmação do agente e da tela — então validação, RLS e mensagens de erro
 * são as mesmas. O passo de agente NÃO escreve nada: ele devolve rascunhos, e
 * cada proposta espera a confirmação de uma pessoa (regra 8).
 */
export async function executar(porta: PortaDeDados, fluxo: Fluxo): Promise<ResultadoDoFluxo> {
    const simulacao = await simular(porta, fluxo);
    const passos = (fluxo.passos ?? []) as PassoDoFluxo[];

    let criadas = 0;
    let repetidas = 0;
    let motivo: string | undefined;
    const rascunhos: RascunhoDoAgente[] = [];

    for (let indice = 0; indice < passos.length; indice += 1) {
        const passo = passos[indice];
        if (!passo) continue;

        if (passo.tipo === 'agente') {
            const alvos = alvosNoPasso(simulacao.casos, indice).slice(0, CASOS_POR_PASSO_DE_AGENTE);
            for (const caso of alvos) {
                try {
                    const resposta = await porta.conversarComAgente({
                        tipo: 'atendimento',
                        mensagens: [{ papel: 'paciente', texto: pedidoAoAgente(passo, caso.campos) }],
                    });
                    rascunhos.push({ passo: passo.id, caso: caso.rotulo, texto: resposta.texto, propostas: resposta.propostas });
                } catch (e) {
                    /* Sem chave, teto estourado ou provedor fora: o caso fica
                       sem rascunho, conta como não executado, e o motivo sobe. */
                    repetidas += 1;
                    motivo ??= e instanceof Error ? e.message : String(e);
                }
            }
            continue;
        }

        if (passo.tipo !== 'acao') continue;
        const acao = acaoDoFluxo(passo.acao);
        const vivos = alvosNoPasso(simulacao.casos, indice);
        if (acao?.bloqueio) {
            /* Ação bloqueada não é erro do fluxo: ela fica esperando o que
               falta, e a contagem devolvida diz que ela não rodou. */
            repetidas += porCaso(passo.acao) ? vivos.length : 1;
            continue;
        }

        const alvos = porCaso(passo.acao) ? vivos : vivos.slice(0, 1);
        for (const caso of alvos) {
            const campos = Object.fromEntries(
                Object.entries(passo.campos).map(([campo, valor]) => [campo, preencher(valor, caso.campos)]),
            );
            try {
                await executarProposta(porta, {
                    ferramenta: passo.acao,
                    rotulo: acao?.rotulo ?? passo.acao,
                    campos: { ...campos, ...(passo.acao === 'followup_criar' ? { paciente: String(caso.campos.nome ?? '') } : {}) },
                });
                criadas += 1;
            } catch (e) {
                /* Um caso que falha não derruba os outros: a clínica prefere
                   nove tarefas criadas a nenhuma. */
                repetidas += 1;
                motivo ??= e instanceof Error ? e.message : String(e);
            }
        }
    }

    const partes: string[] = [];
    if (criadas) partes.push(`${criadas} ${criadas === 1 ? 'ação executada' : 'ações executadas'}`);
    if (rascunhos.length) partes.push(`${rascunhos.length} ${rascunhos.length === 1 ? 'rascunho do agente' : 'rascunhos do agente'} para conferir`);

    return {
        criadas,
        repetidas,
        rascunhos,
        descricao: partes.length
            ? partes.join(' · ')
            : `Nada foi executado: ${motivo ?? 'as ações deste fluxo dependem do que falta.'}`,
    };
}
